/**
 * Sending a slide to another deck by dragging its thumbnail from the sorter (SlidesEditor) onto a
 * part row of the navigation (LeftNav) — AB#207. The two components share nothing else, so this
 * module is the link between them:
 *
 * - the drag itself: the thumbnail's dragstart sets {@link SLIDE_MIME} on the DataTransfer (Firefox
 *   starts no drag without data) and records the source here, because a dragover may read the
 *   types but not the data — and the navigation must refuse the source part while hovering it;
 * - the focus after the drop: the backend answers the new slide's id, the navigation opens the
 *   target part, and the target's editor selects that slide once it has arrived through the CRDT.
 *
 * Plain module state, as the sorter's slide clipboard is: one drag at a time per tab.
 */

/** The DataTransfer type a slide drag carries (JSON {@link SlideDrag}). */
export const SLIDE_MIME = 'application/x-niobe-slide'

export interface SlideDrag {
  room: string
  /** What the drag sends, in deck order: the dragged slide, or the sorter's selection (AB#472). */
  slides: readonly string[]
  /** How many selected slides stay behind because an AI holds them. */
  skipped?: number
}

let dragging: SlideDrag | null = null

export function startSlideDrag(dt: DataTransfer, drag: SlideDrag): void {
  dragging = drag
  dt.setData(SLIDE_MIME, JSON.stringify(drag))
  dt.effectAllowed = 'copyMove'
}

export function endSlideDrag(): void {
  dragging = null
}

/** The slides being dragged out of a sorter, or null: only for a drag that carries {@link SLIDE_MIME}. */
export function slideDragOf(dt: DataTransfer | null): SlideDrag | null {
  if (!dt || !dragging || !Array.from(dt.types ?? []).includes(SLIDE_MIME)) return null
  return dragging
}

/** Copy rather than move: Ctrl held, as file managers do (Alt / Option on macOS). */
export function isCopyDrag(e: { ctrlKey: boolean; altKey: boolean }): boolean {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '')
  return mac ? e.altKey : e.ctrlKey
}

/** The slide to select once its deck opens, and what to say then (one-shot). */
export interface SlideFocus {
  room: string
  slide: string
  /** The polite announcement, with `{n}` for the slide's position in its deck. */
  message: string
  /** The message reports slides left behind: shown as well as spoken. */
  notice?: boolean
}

let focus: SlideFocus | null = null

export function requestSlideFocus(f: SlideFocus): void {
  focus = f
}

/**
 * The pending focus for this deck, CONSUMED once its slide is there (it arrives through the CRDT,
 * possibly after the deck opened): null until then, and for any other room.
 */
export function takeSlideFocus(room: string | undefined, ids: readonly string[]): SlideFocus | null {
  if (!focus || !room || focus.room !== room || !ids.includes(focus.slide)) return null
  const f = focus
  focus = null
  return f
}
